import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Card,
  CardProps,
  IconButton,
  IconButtonProps,
  ListItemButton,
  ListItemButtonProps,
  MenuItem,
  MenuItemProps,
  Link as MuiLink,
  Stack,
  StackProps,
  Tab,
  TabProps,
} from "@mui/joy";
import { createLink } from "@tanstack/react-router";
import { forwardRef } from "react";

const ListItemButtonA = forwardRef<HTMLAnchorElement, ListItemButtonProps<"a">>((props, ref) => (
  <ListItemButton {...props} component="a" ref={ref} />
));
ListItemButtonA.displayName = "ListItemButtonA";

// Joy types MenuItem's ref against its default <div> even when `component`
// is overridden; at runtime the ref is attached to the <a>.
const MenuItemA = forwardRef<HTMLAnchorElement, MenuItemProps<"a">>((props, ref) => (
  <MenuItem {...props} component="a" ref={ref as React.Ref<HTMLDivElement>} />
));
MenuItemA.displayName = "MenuItemA";

// `value` isn't an anchor attribute, but Joy's Button reads it to work out
// aria-pressed inside a ToggleButtonGroup.
const ButtonWrapper = forwardRef<HTMLAnchorElement, ButtonProps<"a"> & Pick<ButtonProps, "value">>(
  (props, ref) => <Button {...props} component="a" ref={ref} />,
);
ButtonWrapper.displayName = "ButtonWrapper";
export const ButtonLink = createLink(ButtonWrapper);

const IconButtonWrapper = forwardRef<HTMLAnchorElement, IconButtonProps<"a">>((props, ref) => (
  <IconButton {...props} component="a" ref={ref} />
));
IconButtonWrapper.displayName = "IconButtonWrapper";

export const IconButtonLink = createLink(IconButtonWrapper);

export const ListItemButtonLink = createLink(ListItemButtonA);
export const MenuItemLink = createLink(MenuItemA);

export const Link = createLink(MuiLink);

type TabRootSlotProps = NonNullable<TabProps["slotProps"]>["root"];

// Joy's Tab takes onClick and onFocus off its props without passing them on,
// which would leave the router's click handler unattached and the anchor doing
// a full page load. Handlers in the root slot's props are merged with the Tab's
// own. Joy types those against its default <button>; at runtime they're
// attached to the <a>.
const TabA = forwardRef<HTMLAnchorElement, TabProps<"a">>(({ onClick, onFocus, ...props }, ref) => (
  <Tab
    {...props}
    component="a"
    ref={ref}
    slotProps={{
      root: { onClick, onFocus } as TabRootSlotProps,
    }}
  />
));
TabA.displayName = "TabA";
export const TabLink = createLink(TabA);

const CardA = forwardRef<HTMLAnchorElement, CardProps<"a">>((props, ref) => (
  <Card {...props} component="a" ref={ref} />
));
CardA.displayName = "CardA";
export const CardLink = createLink(CardA);

const StackA = forwardRef<HTMLAnchorElement, StackProps<"a">>((props, ref) => (
  <Stack {...props} component="a" ref={ref} />
));
StackA.displayName = "StackA";
export const StackLink = createLink(StackA);

const BoxA = forwardRef<HTMLAnchorElement, BoxProps<"a">>((props, ref) => (
  <Box {...props} component="a" ref={ref} />
));
BoxA.displayName = "BoxA";
export const BoxLink = createLink(BoxA);
